import type { ReactElement } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'

import { useAuth } from '../lib/auth'
import { useT } from '../lib/i18n'
import { useBranding } from '../lib/branding'
import { Spinner } from '../components/ui/Spinner'
import { EmptyState } from '../components/ui/EmptyState'
import { ButtonLink } from '../components/ui/Button'

/** Full-page wait while the refresh cookie is being exchanged on boot. */
export const BootScreen = (): ReactElement => {
  const t = useT()
  const { branding } = useBranding()
  return (
    <div className="flex min-h-dvh items-center justify-center bg-paper">
      <div className="flex flex-col items-center gap-3">
        {/* The product name is the tenant's, never translated — only our framing around it. */}
        <Spinner size="lg" label={t('shell.boot.opening', { product: branding.productName })} />
        <p className="t-label text-ink-mute">{branding.productName}</p>
      </div>
    </div>
  )
}

/**
 * Guards `/app`. An unauthenticated visitor is sent to sign in with the path they wanted,
 * so signing in lands them where they were going rather than on a generic dashboard.
 */
export const ProtectedRoute = (): ReactElement => {
  const { status } = useAuth()
  const location = useLocation()

  if (status === 'loading') return <BootScreen />
  if (status === 'anonymous') {
    return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />
  }
  return <Outlet />
}

/** Guards `/app/admin`. Renders a refusal rather than a 404 — the route exists, the access does not. */
export const SuperadminRoute = (): ReactElement => {
  const t = useT()
  const { status, isSuperadmin } = useAuth()

  if (status === 'loading') return <BootScreen />
  if (!isSuperadmin) {
    return (
      <EmptyState
        title={t('shell.superadmin.title')}
        description={t('shell.superadmin.body')}
        action={
          <ButtonLink to="/app/dashboard" variant="primary">
            {t('shell.routeError.backToDashboard')}
          </ButtonLink>
        }
      />
    )
  }
  return <Outlet />
}
